import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, BookOpen, Filter } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES } from "../lib/blog";
import Seo from "../components/Seo";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function BlogList() {
  const [cat, setCat] = useState("Tous");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const posts = useMemo(
    () => (cat === "Tous" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === cat))
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [cat]
  );

  return (
    <main data-testid="blog-list-page" className="bg-white">
      <Seo
        path="/blog"
        title="Blog — Conseils plomberie, serrurerie, électricité"
        description="Guides pratiques et conseils d'experts : dégât des eaux, porte claquée, norme électrique NF C 15-100, détection de fuite, sécurisation après cambriolage."
      />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-14 md:pb-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <span className="chip"><BookOpen className="h-4 w-4" /> Blog & conseils</span>
          <h1 className="mt-4 font-display font-extrabold text-ink text-4xl md:text-6xl leading-[1.05]">
            Guides d&apos;experts <span className="gradient-text">dépannage & sécurité</span>
          </h1>
          <p className="mt-5 text-lg text-ink-soft leading-relaxed max-w-2xl mx-auto">
            Conseils pratiques, prix, méthodes et bonnes pratiques rédigés par nos artisans plombiers,
            serruriers et électriciens en Île-de-France.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-b border-slate-200 bg-white sticky top-16 z-30 backdrop-blur-sm bg-white/90">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-ink-soft" />
          <span className="text-sm text-ink-soft font-semibold">Catégorie :</span>
          {["Tous", ...BLOG_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`chip cursor-pointer transition-colors ${cat === c ? "bg-brand text-white border-brand" : "hover:bg-slate-100"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="card-soft overflow-hidden hover:translate-y-0 group"
              >
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="chip !py-0.5 !px-2">{p.category}</span>
                      <span className="text-ink-soft">·</span>
                      <span className="text-ink-soft">{formatDate(p.date)}</span>
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-ink text-lg md:text-xl leading-tight group-hover:text-brand transition-colors">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-ink-soft text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="text-ink-soft flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.readingTime} min de lecture</span>
                      <span className="text-brand font-semibold flex items-center gap-1">
                        Lire <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
